export enum AppStatusEnum {
  NEW = 'New',
  INREVIEW = 'In Review',
  SUBMITTED = 'Submitted',
  RESUBMITTED = 'Resubmitted',
  RETURNED = 'Returned',
  REJECTED = 'Rejected',
  READYFORFINALREVIEW = 'Ready for Final Review',
  READYFORINTERNALREVIEW = 'Ready for Internal Review',
  APPROVED = 'Approved',
  LEGACY = 'Approved (Legacy)',
  ARCHIVED = 'Archived',
  CLOSED = 'Closed',
  CLOSEDNONRESPONSE = 'Closed (Non-Response)',
  CLOSEDOTHER = 'Closed (Other)',
}

/**
 * Class with helper functions that simplify determinations for feature access (eg. between userTypes, or between Software Only and Full Service)
 */
export class AppStatus {
  private status: AppStatusEnum;

  get NEW() {
    return this.status == AppStatusEnum.NEW;
  }
  get INREVIEW() {
    return this.status == AppStatusEnum.INREVIEW;
  }
  get SUBMITTED() {
    return this.status == AppStatusEnum.SUBMITTED;
  }
  get RESUBMITTED() {
    return this.status == AppStatusEnum.RESUBMITTED;
  }
  get RETURNED() {
    return this.status == AppStatusEnum.RETURNED;
  }
  get REJECTED() {
    return this.status == AppStatusEnum.REJECTED;
  }
  get READYFORFINALREVIEW() {
    return this.status == AppStatusEnum.READYFORFINALREVIEW;
  }
  get READYFORINTERNALREVIEW() {
    return this.status == AppStatusEnum.READYFORINTERNALREVIEW;
  }
  get APPROVED() {
    return this.status == AppStatusEnum.APPROVED;
  }
  get ARCHIVED() {
    return this.status == AppStatusEnum.ARCHIVED;
  }
  get CLOSED() {
    return this.status == AppStatusEnum.CLOSED;
  }
  get CLOSEDNONRESPONSE() {
    return this.status == AppStatusEnum.CLOSEDNONRESPONSE;
  }
  get CLOSEDOTHER() {
    return this.status == AppStatusEnum.CLOSEDOTHER;
  }
  get LEGACY() {
    return this.status == AppStatusEnum.LEGACY;
  }

  constructor(status: AppStatusEnum) {
    this.status = status;
  }

  is(...statuses: string[]) {
    return statuses.some(s => s.toLowerCase().replace(' ', '') == this.status.toLowerCase().replace(' ', ''));
  }

  isNot(status: string) {
    return status.toLowerCase().replace(' ', '') != this.status.toLowerCase().replace(' ', '');
  }

  set(status: AppStatusEnum) {
    this.status = status;
  }

  toString(): AppStatusEnum {
    return this.status;
  }

  isOpen() {
    return [
      AppStatusEnum.INREVIEW,
      AppStatusEnum.SUBMITTED,
      AppStatusEnum.RESUBMITTED,
      AppStatusEnum.RETURNED,
      AppStatusEnum.READYFORINTERNALREVIEW,
      AppStatusEnum.READYFORFINALREVIEW,
    ].includes(this.status);
  }

  isComplete() {
    return [
      AppStatusEnum.APPROVED,
      AppStatusEnum.LEGACY,
      AppStatusEnum.ARCHIVED,
      AppStatusEnum.CLOSED,
      AppStatusEnum.CLOSEDNONRESPONSE,
      AppStatusEnum.CLOSEDOTHER,
      AppStatusEnum.REJECTED,
    ].includes(this.status);
  }

  /**
   * @returns true if the app is in the court of a Full Service Agency. (The only status where this is the case is Ready For Final Review)
   */
  isInFSAgencyCourt() {
    return this.status == AppStatusEnum.READYFORFINALREVIEW;
  }

  /**
   * @returns true if the app is in the court of a Software Only Agency.
   */
  isInSOAgencyCourt() {
    return [
      AppStatusEnum.INREVIEW,
      AppStatusEnum.SUBMITTED,
      AppStatusEnum.RESUBMITTED,
      AppStatusEnum.APPROVED,
    ].includes(this.status);
  }

  /**
   * @returns true if the app is in the court of LAUSD
   */
  isInLausdCourt() {
    return [
      AppStatusEnum.INREVIEW,
      AppStatusEnum.SUBMITTED,
      AppStatusEnum.RESUBMITTED,
      AppStatusEnum.READYFORFINALREVIEW,
      AppStatusEnum.APPROVED,
    ].includes(this.status);
  }

  /**
   * @returns true if the app is in the main review status - Submitted, Resubmitted, or In Review. (No Internal Review or Final Review)
   */
  isInMainReview() {
    return [AppStatusEnum.INREVIEW, AppStatusEnum.SUBMITTED, AppStatusEnum.RESUBMITTED].includes(this.status);
  }

  /**
   * @returns true if the vendor can act on this app. This is only New or Returned.
   */
  isInVendorCourt() {
    return [AppStatusEnum.NEW, AppStatusEnum.RETURNED].includes(this.status);
  }

  /**
   * @returns true if a QBAT user can act on this app.
   */
  isInQbatCourt() {
    return [
      AppStatusEnum.INREVIEW,
      AppStatusEnum.SUBMITTED,
      AppStatusEnum.RESUBMITTED,
      AppStatusEnum.READYFORINTERNALREVIEW,
    ].includes(this.status);
  }

  /**
   * @returns true if the app has been submitted.
   */
  isSubmitted() {
    return !this.NEW;
  }
}
